


import {
  defineComponent,
  PropType,
  onMounted,
  computed,
  reactive,
} from '@vue/composition-api';
import type {
  HokkaidoAirportReportCar,
  HokkaidoAirportReportPhotoCount,
} from '@/models/externalReports/hokkaidoAirport/spHokkaidoAirportReport';
import type {
  HokkaidoAirportMtxRequest,
} from '@/models/apis/externalReports/hokkaidoAirport/hokkaidoAirportMtxRequest';
import type { ImageCandidateBase as ImageCandidate } from '@/models/apis/image/imageRequest';
import type {
  HokkaidoAirportReportPhotoParams,
} from '@/models/apis/externalReports/hokkaidoAirport/hokkaidoAirportReportRequest';
import type {
  HokkaidoAirportSettings,
  HokkaidoAirportLabelSp,
} from '@/models/apis/externalReports/hokkaidoAirport/hokkaidoAirportSettings';
import { useRequestGuard } from '@/composables/useRequestGuard';
import PhotoTaker from '@/components/lib/PhotoTaker.vue';
import hokkaidoAirportReportApi from '@/apis/externalReports/hokkaidoAirport/hokkaido_airport_report';
import { getImageCandidate } from '@/lib/imageHelper';
import { dtFormat } from '@/lib/dateHelper';
import ConfirmModal from '@/components/lib/ConfirmModal.vue';
import {
  MAX_SAVABLE_PHOTOS_COUNT,
  MAX_PENDING_PHOTOS_COUNT,
} from '@/consts/externalReports/hokkaidoAirport';

interface PhotoMemoState {
  photoType: string;
  comment: string;
  photoCounts: HokkaidoAirportReportPhotoCount[];
  imageCandidates: ImageCandidate[];
  errorModalMsg: string;
  pendingPhotosCountBeforeSave: number;
  showCommonErrorModal: boolean;
  showSavePhotosConfirmModal: boolean;
  showRevertSavePhotosConfirmModal: boolean;
  showPhotoSaveErrorModal: boolean;
}

export default defineComponent({
  props: {
    hokkaidoAirportCar: {
      type: Object as PropType<HokkaidoAirportReportCar>,
      required: true,
    },
    userSettings: {
      type: Object as PropType<HokkaidoAirportSettings>,
      required: true,
    },
    hokkaidoAirportMtxs: {
      type: Array as PropType<HokkaidoAirportMtxRequest[]>,
      required: true,
    },
  },
  components: {
    PhotoTaker,
    ConfirmModal,
  },
  emits: [
    'revertScreen',
    'updateCommon',
    'showErrorOnCommon',
  ],
  setup(props, { emit }) {
    const state = reactive<PhotoMemoState>({
      photoType: '',
      comment: '',
      photoCounts: [],
      imageCandidates: [],
      errorModalMsg: '',
      pendingPhotosCountBeforeSave: 0,
      showCommonErrorModal: false,
      showSavePhotosConfirmModal: false,
      showRevertSavePhotosConfirmModal: false,
      showPhotoSaveErrorModal: false,
    });
    const { doRequest, isRequesting } = useRequestGuard((errorMessage) => {
      emit('showErrorOnCommon', errorMessage);
    });

    const calculatePhotoCount = computed<number>(() => {
      const currentTypePhotoCount = state.photoCounts.find(item => item.photoType === state.photoType)?.count ?? 0;
      return currentTypePhotoCount + state.imageCandidates.length;
    });
    const photoTypes = computed(() => {
      return props.userSettings.choices.photo_types.map((e: string) => {
        return {
          name: e,
          value: e,
        };
      });
    });
    const canSaveAdditionalPhoto = computed<boolean>(() => {
      return calculatePhotoCount.value <= MAX_SAVABLE_PHOTOS_COUNT;
    });
    const canAddPendingPhoto = computed<boolean>(() => {
      return state.imageCandidates.length < MAX_PENDING_PHOTOS_COUNT &&
        calculatePhotoCount.value < MAX_SAVABLE_PHOTOS_COUNT;
    });
    const hasPhotos = computed<boolean>(() => {
      return state.imageCandidates.length > 0 && state.photoType !== '';
    });
    const canSavePhotos = computed<boolean>(() => {
      return !isRequesting.value &&
        canSaveAdditionalPhoto.value &&
        state.photoType !== '' &&
        (hasPhotos.value || state.comment !== '');
    });
    const imageCandidateCount = computed<number>(() => {
      return state.imageCandidates.length;
    });
    const spLabels = computed<HokkaidoAirportLabelSp>(() => {
      return props.userSettings.sp;
    });
    const fileNamePrefixForDownload = computed<string>(() => {
      return `HokkaidoAirport_${spLabels.value.title.operation_name}_${state.photoType}`;
    });
    const pendingPhotosCount = computed<number>(() => {
      return state.imageCandidates.length;
    });

    const attachImageFile = async(file: File) => {
      if (!canSaveAdditionalPhoto.value) {
        state.errorModalMsg = '添付画像の最大数は' + MAX_SAVABLE_PHOTOS_COUNT + '枚です。';
        state.showCommonErrorModal = true;
        return;
      }

      const imageCandidate = await getImageCandidate(file);
      if (!imageCandidate) return;
      state.imageCandidates.push(imageCandidate);
    };
    const onImageFileAttach = async(evt: Event) => {
      const fileList = (evt.target as HTMLInputElement).files;
      if (!fileList || fileList.length === 0) { return; }
      const file = fileList[0];
      attachImageFile(file);
    };
    const removeImageCandidate = (idx: number) => {
      URL.revokeObjectURL(state.imageCandidates[idx].src);
      state.imageCandidates.splice(idx, 1);
    };
    const tryRevertEditImages = () => {
      if (state.imageCandidates.length === 0) {
        revertScreen();
        return;
      }
      state.showRevertSavePhotosConfirmModal = true;
    };
    const revertScreen = () => {
      emit('revertScreen');
    };
    const sendImagesInner = async() => {
      const hokkaidoAirportReport = props.hokkaidoAirportCar.report;
      state.pendingPhotosCountBeforeSave = imageCandidateCount.value;
      const canSend = state.comment !== '' || hasPhotos.value;
      if (hokkaidoAirportReport.id === 0 || !canSend) return;

      const mtxSlice = props.hokkaidoAirportMtxs.slice(0);
      const mtxLength = mtxSlice.length;
      if (mtxLength === 0) {
        state.errorModalMsg = '位置情報が取得できません。再度操作を行ってください';
        state.showCommonErrorModal = true;
        return;
      }
      const lastMtx = mtxSlice.slice(mtxLength - 1, mtxLength)[0];

      let remainingImageCandidates: ImageCandidate[] = [];
      const reqObj: HokkaidoAirportReportPhotoParams = {
        ts: dtFormat(lastMtx.ts),
        lat: lastMtx.lat,
        lon: lastMtx.lon,
        photo_type: state.photoType,
        comment: state.comment,
        image: null,
      };
      if (!hasPhotos.value) {
        // 写真メモのみ送信の場合は、コメントを更新
        await doRequest(async() => {
          await hokkaidoAirportReportApi.addPhoto(hokkaidoAirportReport.id, reqObj);
        }, '写真メモの更新に失敗しました。再度操作を行ってください。');
        emit('updateCommon', props.userSettings.choices.status.working.key, props.hokkaidoAirportCar.report);
        return;
      }

      for (const [index, imageCandidate] of state.imageCandidates.entries()) {
        try {
          reqObj.image = imageCandidate.file;
          await doRequest(async() => {
            await hokkaidoAirportReportApi.addPhoto(hokkaidoAirportReport.id, reqObj);
          });
          URL.revokeObjectURL(imageCandidate.src);
        } catch (e) {
          remainingImageCandidates = state.imageCandidates.slice(index);
          // 一つでも失敗したらユーザーに再送信を促す
          break;
        }
      }
      state.imageCandidates = remainingImageCandidates;
      if (state.imageCandidates.length === 0) {
        emit('updateCommon', props.userSettings.choices.status.working.key, props.hokkaidoAirportCar.report);
      }
    };
    const sendImages = async() => {
      await sendImagesInner();

      // 写真の送信失敗処理
      if (state.imageCandidates.length > 0) {
        state.showSavePhotosConfirmModal = false;
        state.showPhotoSaveErrorModal = true;
        return;
      }
      state.showSavePhotosConfirmModal = false;
    };

    onMounted(async() => {
      await doRequest(async() => {
        state.photoCounts = await hokkaidoAirportReportApi.getPhotoCount(props.hokkaidoAirportCar.report.id);
      }, '写真の取得に失敗しました。再度操作を行ってください');
      state.photoType = photoTypes.value[0].value;
    });

    return {
      state,
      canAddPendingPhoto,
      canSavePhotos,
      fileNamePrefixForDownload,
      hasPhotos,
      isRequesting,
      imageCandidateCount,
      spLabels,
      photoTypes,
      pendingPhotosCount,
      attachImageFile,
      onImageFileAttach,
      removeImageCandidate,
      revertScreen,
      sendImages,
      tryRevertEditImages,
    };
  },
});
